import React, { useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Input Component
export const Input = ({ 
  label, 
  type = 'text', 
  required = false, 
  error, 
  help,
  icon: Icon,
  ...props 
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
        )}
        <input
          type={type}
          className={`
            input ${Icon ? 'pl-10' : ''}
            ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
          `}
          {...props}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {help && <p className="mt-1 text-sm text-gray-500">{help}</p>}
    </div>
  );
};

// Textarea Component
export const Textarea = ({ 
  label, 
  required = false, 
  error, 
  help,
  rows = 3,
  ...props 
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <textarea
        rows={rows}
        className={`
          input resize-none
          ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
        `}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {help && <p className="mt-1 text-sm text-gray-500">{help}</p>}
    </div>
  );
};

// Select Component
export const Select = ({ 
  label, 
  options = [], 
  required = false, 
  error, 
  help,
  placeholder = "בחר אפשרות",
  ...props 
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <select
        className={`
          input
          ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
        `}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {help && <p className="mt-1 text-sm text-gray-500">{help}</p>}
    </div>
  );
};

// Checkbox Component
export const Checkbox = ({ 
  label, 
  checked, 
  onChange, 
  error, 
  help,
  ...props 
}) => {
  return (
    <div className="mb-4">
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className={`
            h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded
            ${error ? 'border-red-300' : ''}
          `}
          {...props}
        />
        {label && (
          <label className="mr-2 text-sm text-gray-700">
            {label}
          </label>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {help && <p className="mt-1 text-sm text-gray-500">{help}</p>}
    </div>
  );
};

// Image Upload Component
export const ImageUpload = ({ 
  label, 
  images = [], 
  onImagesChange, 
  maxImages = 5, 
  error, 
  help 
}) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (files) => {
    const fileList = Array.from(files);
    const totalImages = images.length + fileList.length;
    
    if (totalImages > maxImages) {
      alert(`ניתן להעלות עד ${maxImages} תמונות`);
      return;
    }

    fileList.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newImage = {
            id: Date.now() + Math.random(),
            file: file,
            url: e.target.result,
            name: file.name
          };
          onImagesChange([...images, newImage]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeImage = (imageId) => {
    onImagesChange(images.filter(img => img.id !== imageId));
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300'}
          ${error ? 'border-red-300' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-upload').click()}
      >
        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          גרור תמונות לכאן או לחץ להעלאה
        </p>
        <p className="text-xs text-gray-500">
          PNG, JPG, WEBP עד 5MB (מקסימום {maxImages} תמונות)
        </p>
        <input
          id="file-upload"
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </div>

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          {images.map((image) => (
            <div key={image.id} className="relative">
              <img
                src={image.url}
                alt={image.name}
                className="w-full h-24 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => removeImage(image.id)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {help && <p className="mt-2 text-sm text-gray-500">{help}</p>}
    </div>
  );
};

// Form Buttons
export const FormButtons = ({ 
  onCancel, 
  onSubmit, 
  submitText = "שמור", 
  cancelText = "ביטול", 
  loading = false,
  submitDisabled = false 
}) => {
  return (
    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={onCancel}
        className="btn btn-outline"
        disabled={loading}
      >
        {cancelText}
      </button>
      <button
        type="submit"
        onClick={onSubmit}
        disabled={loading || submitDisabled}
        className="btn btn-primary flex items-center"
      >
        {loading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2" />
        )}
        {submitText}
      </button>
    </div>
  );
};
